'use client';

import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import {
  PRIMARY_NAV_COLLAPSED_WIDTH,
  PRIMARY_NAV_DURATION,
  PRIMARY_NAV_EXPANDED_WIDTH,
  PRIMARY_NAV_Z_INDEX,
  useGetPrimaryNavBaseWidth,
  usePrimaryNavState,
} from '@frameio/primary-nav';
import { useContextMenuIsOpen } from '@frameio/shared/contexts';
import { AppSheetContext } from '@frameio/shared/contexts/AppSheetContext';
import { useIsMobileNavClosing } from '@frameio/shared/contexts/AppStateContext/appStateSelectors';
import {
  useDispatchAppState,
  useIsMobileNavOpen,
} from '@frameio/shared/contexts/AppStateContext/index';
import { HeaderAnchorContext } from '@frameio/shared/contexts/HeaderAnchorContext';
import { Overlay, theme } from '@frameio/vapor';

const appLayoutPadding = '2px';

const PRIMARY_NAV_STYLE_OVERRIDE = {
  width: '64px',
};

export const AppLayoutGrid = ({ children }: PropsWithChildren) => {
  const baseWidth = useGetPrimaryNavBaseWidth();

  return (
    <StyledAppLayoutGrid $navColumnWidth={baseWidth}>
      {children}
    </StyledAppLayoutGrid>
  );
};

const StyledAppLayoutGrid = styled.div<{ $navColumnWidth: number }>`
  box-sizing: border-box;
  display: grid;
  grid-template-areas:
    'top-bar'
    'banner'
    'header'
    'main';
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto auto 1fr;
  height: 100%;
  overflow: clip;
  padding: ${appLayoutPadding};
  padding-top: 1px;
  width: 100%;

  ${theme.mediaQueries.tablet} {
    column-gap: 2px;
    grid-template-areas:
      'top-bar top-bar'
      'banner banner'
      'primary-nav header'
      'primary-nav main';
    grid-template-columns: ${(p) => `${p.$navColumnWidth}px`} minmax(0, 1fr);
    transition: grid-template-columns ${PRIMARY_NAV_DURATION / 1000}s ease-out;
  }
`;

export function AppLayoutGridPrimaryNavArea(props: ComponentProps<'div'>) {
  const isMobileNavOpen = useIsMobileNavOpen();
  const dispatch = useDispatchAppState();
  const isMobileNavClosing = useIsMobileNavClosing();
  const { setId: setSheetId, id: sheetId } = useContext(AppSheetContext);
  const isGlobalNavEnabled = useFeatureFlagEnabled('v4.global_navigation');
  const baseWidth = useGetPrimaryNavBaseWidth();
  const navState = usePrimaryNavState();
  const forcedOpen = useContextMenuIsOpen();

  // @NOTE (FIND-2557): This is a temporary measure to ensure that the prvious nav still works.
  // I am not able to do this in the layout file as that is a server component, so here I am
  // injecting the width property if the new global navigation is not enabled.
  const style = useMemo(() => {
    const result: CSSProperties = { ...props.style };

    if (!isGlobalNavEnabled) {
      Object.assign(result, PRIMARY_NAV_STYLE_OVERRIDE);
    }

    return result;
  }, [props.style, isGlobalNavEnabled]);

  const closeMobileNav = useCallback(() => {
    dispatch({ type: 'CLOSE_MOBILE_NAV' });
    setSheetId?.(null);
  }, [dispatch, setSheetId]);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (event.currentTarget === event.target) {
        closeMobileNav();
      }
    },
    [closeMobileNav]
  );

  const Container = useMemo(() => {
    if (!isGlobalNavEnabled) {
      return (
        <AppLayoutGridPrimaryNavAreaContainer
          $shouldDelayTransition={isMobileNavClosing}
          $xOffset={
            isMobileNavOpen ? '0px' : `calc(-100% - ${appLayoutPadding})`
          }
          onClick={handleClick}
          {...props}
          style={style}
        />
      );
    }

    const isSheetActive = sheetId !== null;
    const isNavExpanded = navState === 'expanded';
    const flyoutWidth =
      isSheetActive && !isNavExpanded && !forcedOpen
        ? PRIMARY_NAV_COLLAPSED_WIDTH
        : PRIMARY_NAV_EXPANDED_WIDTH;

    return (
      <AppLayoutGridPrimaryNavAreaFlyoutContainer
        $baseWidth={forcedOpen ? flyoutWidth : baseWidth}
        $flyoutWidth={flyoutWidth}
        $shouldDelayTransition={isMobileNavClosing}
        $xOffset={isMobileNavOpen ? '0px' : `calc(-100% - ${appLayoutPadding})`}
        onClick={handleClick}
        {...props}
        style={style}
      />
    );
  }, [
    isGlobalNavEnabled,
    forcedOpen,
    baseWidth,
    handleClick,
    isMobileNavOpen,
    isMobileNavClosing,
    props,
    navState,
    sheetId,
    style,
  ]);

  return (
    <>
      {Container}
      <BaseOnly>
        <StyledGradient $opacity={isMobileNavOpen ? 1 : 0} />
        <Overlay
          isShowing={isMobileNavOpen}
          onRequestClose={closeMobileNav}
          zIndex={theme.zIndices.overlay}
        />
      </BaseOnly>
    </>
  );
}

const BaseOnly = styled.div`
  ${theme.mediaQueries.tablet} {
    display: none;
  }
`;

/**
 * This gradient covers the mobile nav overlay with an additional background to
 * further dim the assets and prevent them showing through panel gaps.
 * @todo (VAPR-432): update background colors with Fuse theme when available
 */
const StyledGradient = styled.div<{ $opacity: number }>`
  background-image: linear-gradient(
    90deg,
    ${theme.colors.background.base} 0%,
    transparent 40%
  );
  bottom: 0;
  left: 0;
  opacity: ${(props) => props.$opacity};
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  transition: opacity 0.2s ${theme.transitions.eases.primary};
  z-index: ${theme.zIndices.overlay};
`;

const AppLayoutGridPrimaryNavAreaContainer = styled.nav<{
  $xOffset: string;
  $shouldDelayTransition: boolean;
}>`
  -ms-overflow-style: none;
  border-radius: ${theme.radii[8]};
  border-top: ${theme.spaces[2]} solid ${theme.colors.background.base};
  display: flex;
  grid-area: header / header / main / main;
  height: 100%;
  overflow: auto;
  position: relative;
  scrollbar-width: none;
  transform: translateX(${(props) => props.$xOffset});
  transition-delay: ${(p) =>
    p.$shouldDelayTransition
      ? theme.transitions.durations[PRIMARY_NAV_DURATION]
      : 0};
  transition-duration: ${theme.transitions.durations[PRIMARY_NAV_DURATION]};

  transition-timing-function: ${theme.transitions.eases.primary};
  z-index: ${theme.zIndices.popover};
  &::-webkit-scrollbar {
    display: none;
  }

  ${theme.mediaQueries.tablet} {
    flex-direction: column;
    grid-area: primary-nav;
    transform: none;
    transition: none;
    z-index: ${theme.zIndices.panel};
  }
`;

const AppLayoutGridPrimaryNavAreaFlyoutContainer = styled.nav<{
  $xOffset: string;
  $shouldDelayTransition: boolean;
  $baseWidth: number;
  $flyoutWidth: number;
}>`
  -ms-overflow-style: none;
  border-radius: ${theme.radii[8]};
  border-top: ${theme.spaces[2]} solid ${theme.colors.background.base};
  display: flex;
  grid-area: header / header / main / main;
  height: 100%;
  overflow: auto;
  position: relative;
  scrollbar-width: none;
  transform: translateX(${(props) => props.$xOffset});
  transition-delay: ${(p) =>
    p.$shouldDelayTransition
      ? theme.transitions.durations[PRIMARY_NAV_DURATION]
      : 0};
  transition-duration: ${theme.transitions.durations[PRIMARY_NAV_DURATION]};

  transition-timing-function: ${theme.transitions.eases.primary};
  z-index: ${theme.zIndices.popover};
  &::-webkit-scrollbar {
    display: none;
  }

  ${theme.mediaQueries.tablet} {
    background: ${theme.colors.background.base};
    flex-direction: column;
    grid-area: primary-nav;
    transform: none;
    transition: width ${PRIMARY_NAV_DURATION / 1000}s
      ${theme.transitions.eases.primary};
    width: ${(p) => `${p.$baseWidth}px`};
    z-index: ${PRIMARY_NAV_Z_INDEX};

    @media (hover: hover) {
      &:hover {
        width: ${(p) => p.$flyoutWidth}px;
      }
    }
  }
`;

export const AppLayoutGridHeaderArea = ({
  children,
  ...props
}: ComponentPropsWithoutRef<typeof StyledAppLayoutGridHeaderArea>) => {
  const anchorRef = useRef<HTMLElement>(null);

  return (
    <HeaderAnchorContext.Provider value={anchorRef}>
      <StyledAppLayoutGridHeaderArea {...props} ref={anchorRef}>
        {children}
      </StyledAppLayoutGridHeaderArea>
    </HeaderAnchorContext.Provider>
  );
};

const StyledAppLayoutGridHeaderArea = styled.header<{ $isWebClient: boolean }>`
  background-color: ${(p) =>
    p.$isWebClient ? undefined : theme.colors.background.panel};
  grid-area: header;
  min-width: 0;
`;

export const AppLayoutGridMainArea = styled.div<{
  $isBorderEnabled: boolean;
  $isWebClient: boolean;
}>`
  border-top: ${(p) => {
    if (!p.$isBorderEnabled) {
      return 'none';
    }
    return p.$isWebClient
      ? `1px solid ${theme.colors.background.base}`
      : `1px solid ${theme.colors.border.primary}`;
  }};
  grid-area: main;
  margin: -1px;
  min-height: 0;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  width: calc(100% + 2px);
`;

export const AppLayoutAdobeTopBarArea = styled.section`
  grid-area: top-bar;
  /* offset padding around the layout grid container so bar is flush against viewport */
  margin-left: -${appLayoutPadding};
  margin-top: -1px;
  width: calc(100% + (${appLayoutPadding} * 2));
`;
